import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CheckCircle, AlertCircle, Calendar, MessageSquare, Share2, Mail } from 'lucide-react';
import { Loader } from '@mantine/core';
  const getNotificationIcon = (notification: Notification) => {
    switch (notification.type) {
      case 'task_assigned':
        return <AlertCircle size={16} className="icon-blue" />;
      case 'task_completed':
        return <CheckCircle size={16} className="icon-green" />;
      case 'task_priority_changed':
        return <AlertCircle size={16} className="icon-orange" />;
      case 'task_due_date_changed':
        return <Calendar size={16} className="icon-purple" />;
      case 'task_comment_added':
      case 'comment_added':
        return <MessageSquare size={16} className="icon-blue" />;
      case 'list_shared':
        return <Share2 size={16} className="icon-green" />;
      case 'invitation_received':
        return <Mail size={16} className="icon-blue" />;
      case 'invitation_accepted':
        return <CheckCircle size={16} className="icon-green" />;
      case 'task_created':
        return <AlertCircle size={16} className="icon-green" />;
      case 'task_overdue':
        return <AlertCircle size={16} className="icon-red" />;
      case 'task_due_soon':
        return <Calendar size={16} className="icon-orange" />;
      case 'task_unassigned':
        return <AlertCircle size={16} className="icon-gray" />;
      default:
        return <AlertCircle size={16} className="icon-gray" />;
    }
  };
import { formatDistanceToNow } from 'date-fns';
import { Notification, TaskNotification, ListNotification, InvitationNotification, InvitationAcceptedNotification } from '../types/notifications';
import './NotificationPanel.css';

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
  notifications: Notification[];
  onMarkAsRead: (notificationId: number) => void;
  onMarkAllAsRead: () => void;
  onNotificationClick: (notification: Notification) => void;
  onNotificationRemove: (notificationId: number) => void;
  onRemoveAllNotifications: () => void;
  isLoading?: boolean;
  error?: Error | null;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onNotificationClick,
  onNotificationRemove,
  onRemoveAllNotifications,
  isLoading = false,
  error = null,
}) => {
  const navigate = useNavigate();

  // Close panel on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);



  const formatNotificationMessage = (notification: Notification) => {
    // Helper to highlight email
    const highlightEmail = (email: string) => (
      <span className="highlight-email">{email}</span>
    );
    // Helper to highlight name
    const highlightName = (name: string) => (
      <span className="highlight-name">{name}</span>
    );
    // Helper to highlight list name
    const highlightList = (list: string) => (
      <span className="highlight-list">{list}</span>
    );

    switch (notification.type) {
      case 'task_assigned': {
        const { actionBy, taskTitle } = notification as TaskNotification;
        return <>{highlightEmail(actionBy)} assigned you to "{taskTitle}"</>;
      }
      case 'task_completed': {
        const { actionBy, taskTitle } = notification as TaskNotification;
        return <>{highlightEmail(actionBy)} completed "{taskTitle}"</>;
      }
      case 'task_priority_changed': {
        const { actionBy, taskTitle, previousValue, newValue } = notification as TaskNotification;
        return <>{highlightEmail(actionBy)} changed priority of "{taskTitle}" from <span className="highlight-name">{previousValue}</span> to <span className="highlight-name">{newValue}</span></>;
      }
      case 'task_due_date_changed': {
        const { actionBy, taskTitle } = notification as TaskNotification;
        return <>{highlightEmail(actionBy)} changed due date of "{taskTitle}"</>;
      }
      case 'task_comment_added':
      case 'comment_added': {
        const { actionBy, taskTitle } = notification as TaskNotification;
        return <>{highlightEmail(actionBy)} commented on "{taskTitle}"</>;
      }
      case 'task_created': {
        const { actionBy, taskTitle } = notification as TaskNotification;
        return <>{highlightEmail(actionBy)} created task "{taskTitle}"</>;
      }
      case 'task_overdue': {
        const { taskTitle } = notification as TaskNotification;
        return <>Task "{taskTitle}" is overdue</>;
      }
      case 'task_due_soon': {
        const { taskTitle } = notification as TaskNotification;
        return <>Task "{taskTitle}" is due soon</>;
      }
      case 'task_unassigned': {
        const { actionBy, taskTitle } = notification as TaskNotification;
        return <>{highlightEmail(actionBy)} unassigned you from "{taskTitle}"</>;
      }
      case 'list_shared': {
        const { actionBy, listName } = notification as ListNotification;
        return <>{highlightEmail(actionBy!)} shared list {listName ? highlightList(listName!) : 'a list'} with you</>;
      }
      case 'invitation_received': {
        const invitation = notification as InvitationNotification;
        if (invitation.inviterName && invitation.inviterEmail) {
          return <>{highlightName(invitation.inviterName)} ({highlightEmail(invitation.inviterEmail)}) invited you to collaborate</>;
        } else if (invitation.inviterName) {
          return <>{highlightName(invitation.inviterName)} invited you to collaborate</>;
        } else if (invitation.inviterEmail) {
          return <>{highlightEmail(invitation.inviterEmail)} invited you to collaborate</>;
        } else if (invitation.userId) {
          return <>{highlightEmail(invitation.userId)} invited you to collaborate</>;
        } else {
          return <>Someone invited you to collaborate</>;
        }
      }
      case 'invitation_accepted': {
        const invitation = notification as InvitationAcceptedNotification;
        // Try to get accepter info from metadata first, then fall back to notification properties
        let accepterName = invitation.accepterName;
        let accepterEmail = invitation.accepterEmail;
        
        if (notification.metadata) {
          try {
            const metadata = typeof notification.metadata === 'string' 
              ? JSON.parse(notification.metadata) 
              : notification.metadata;
            accepterName = metadata.accepterName || accepterName;
            accepterEmail = metadata.accepterEmail || accepterEmail;
          } catch (e) {
            // Fall back to notification properties if metadata parsing fails
          }
        }
        
        if (accepterName && accepterEmail) {
          return <>{highlightName(accepterName)} ({highlightEmail(accepterEmail)}) accepted your invitation to collaborate</>;
        } else if (accepterName) {
          return <>{highlightName(accepterName)} accepted your invitation to collaborate</>;
        } else if (accepterEmail) {
          return <>{highlightEmail(accepterEmail)} accepted your invitation to collaborate</>;
        } else {
          return <>Someone accepted your invitation to collaborate</>;
        }
      }
      default: {
        // Try to highlight emails and list names in fallback message
        const msg = (notification as any).message || 'New notification';
        // Simple regex-based highlighting for fallback
        const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
        const listRegex = /"([^"]+)"/g;
        let parts: (string | JSX.Element)[] = [];
        let lastIndex = 0;
        let match;
        let msgCopy = msg;
        // Highlight emails
        while ((match = emailRegex.exec(msgCopy)) !== null) {
          if (match.index > lastIndex) parts.push(msgCopy.slice(lastIndex, match.index));
          parts.push(highlightEmail(match[1]));
          lastIndex = match.index + match[1].length;
        }
        if (lastIndex < msgCopy.length) parts.push(msgCopy.slice(lastIndex));
        // Highlight list names in quotes
        parts = parts.flatMap(part => {
          if (typeof part === 'string') {
            const subparts: (string | JSX.Element)[] = [];
            let last = 0;
            let m;
            while ((m = listRegex.exec(part)) !== null) {
              if (m.index > last) subparts.push(part.slice(last, m.index));
              subparts.push(highlightList(m[1]));
              last = m.index + m[0].length;
            }
            if (last < part.length) subparts.push(part.slice(last));
            return subparts;
          }
          return part;
        });
        return <>{parts}</>;
      }
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      onMarkAsRead(notification.id);
    }
    // Special navigation for invitation notifications
    if (notification.type === 'invitation_received' || notification.type === 'invitation_accepted') {
      onClose();
      navigate('/settings', { state: { section: 'contacts' } });
    } else {
      onNotificationClick(notification);
    }
  };

  const unreadCount = notifications?.filter(n => !n.isRead)?.length || 0;

  return (
      <>
        {/* Backdrop */}
        {isOpen && (
          <div 
            className="notification-backdrop" 
            onClick={onClose}
            aria-hidden="true"
          />
        )}
        
        {/* Panel */}
        <div className={`notification-panel ${isOpen ? 'open' : ''}`}>
        <div className="notification-header">
          <h2>Notifications</h2>
          <div className="notification-header-actions">
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={onMarkAllAsRead}
                disabled={isLoading}
              >
                Mark all as read
              </button>
            )}
            {notifications.length > 0 && (
              <button
                className="remove-all-btn"
                disabled={isLoading}
                onClick={() => {
                  if (window.confirm('Are you sure you want to remove all notifications?')) {
                    onRemoveAllNotifications();
                  }
                }}
                aria-label="Remove all notifications"
              >
                Remove all
              </button>
            )}
            <button
              className="close-btn tooltip-wrapper"
              data-tooltip="Close notifications"
              onClick={onClose}
              aria-label="Close notifications"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="notification-content">
          {isLoading ? (
            <div className="loading-notifications">
              <Loader type="bars" size="md" />
              <p>Loading notifications...</p>
            </div>
          ) : error ? (
            <div className="error-notifications">
              <AlertCircle size={48} className="icon-red" />
              <p>Failed to load notifications</p>
              <span>{error.message}</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="no-notifications">
              <AlertCircle size={48} className="icon-gray" />
              <p>No notifications yet</p>
              <span>You'll see activity updates here</span>
            </div>
          ) : (
            <div className="notification-list">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleNotificationClick(notification);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Notification: ${formatNotificationMessage(notification)}`}
                  style={!notification.isRead ? { background: 'rgba(56, 189, 248, 0.12)', borderLeft: '4px solid #0ea5e9', fontWeight: 600 } : {}}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification)}
                  </div>
                  <div className="notification-body">
                    {/* Header: list name if present */}
                    {(notification.type.startsWith('task_') || notification.type === 'list_shared') && 'listName' in notification && notification.listName && (
                      <div className="notification-list-name-header">
                        {notification.listName}
                      </div>
                    )}
                    {/* Main message */}
                    <div className="notification-message">
                      {formatNotificationMessage(notification)}
                    </div>
                    {/* Footer: time ago */}
                    <div className="notification-meta-row">
                      <span className="notification-time">
                        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                  {/* Remove button in top right */}
                  <button
                    className="notification-remove-btn tooltip-wrapper"
                    data-tooltip="Remove notification"
                    onClick={e => {
                      e.stopPropagation();
                      onNotificationRemove(notification.id);
                    }}
                    aria-label="Remove notification"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </>
    );
};